import React, { useState } from 'react';
import { Map, MapPin, Navigation, AlertTriangle, ExternalLink, Copy, Eye, EyeOff } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения информации о местоположении съемки
 */
const LocationViewer = ({ location, imageUrl }) => {
  const [mapVisible, setMapVisible] = useState(true);
  
  if (!location || !location.latitude || !location.longitude) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
          <MapPin className="w-6 h-6 mr-2" />
          <span>Данные о местоположении отсутствуют</span>
        </div>
      </div>
    );
  }

  // Функция для копирования координат
  const copyCoordinates = () => {
    const coordsText = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
    navigator.clipboard.writeText(coordsText);
    alert('Координаты скопированы в буфер обмена');
  };

  // Функция для открытия карты в Яндекс.Картах (популярный сервис в России)
  const openInYandexMaps = () => {
    const url = `https://yandex.ru/maps/?ll=${location.longitude.toFixed(6)},${location.latitude.toFixed(6)}&z=15`;
    window.open(url, '_blank');
  };

  // Функция для открытия карты в Google Maps
  const openInGoogleMaps = () => {
    const url = `https://www.google.com/maps?q=${location.latitude.toFixed(6)},${location.longitude.toFixed(6)}`;
    window.open(url, '_blank');
  };

  // Генерация статической карты
  // В реальном приложении здесь бы использовался настоящий API карт
  const staticMapUrl = `https://static-maps.yandex.ru/1.x/?ll=${location.longitude.toFixed(6)},${location.latitude.toFixed(6)}&size=650,300&z=13&l=map&pt=${location.longitude.toFixed(6)},${location.latitude.toFixed(6)},pm2rdm`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Основная информация */}
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Map className="w-5 h-5 mr-2 text-purple-500" />
          Информация о местоположении съемки
        </h2>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="text-red-500 w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                Внимание! Обнаружены данные о местоположении
              </h3>
              <p className="text-sm text-red-700 dark:text-red-400">
                Данное изображение содержит GPS-координаты места съемки. Если вы планируете опубликовать это изображение, 
                рекомендуем удалить эти данные для сохранения конфиденциальности.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Координаты и информация */}
          <div>
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-purple-500" />
              Координаты местоположения
            </h3>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-3">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Широта
                </span>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {location.latitude.toFixed(6)}° 
                  </span>
                  <button 
                    onClick={copyCoordinates}
                    className="ml-2 p-1 text-gray-400 hover:text-purple-500 dark:text-gray-500 dark:hover:text-purple-400 transition-colors"
                    title="Копировать координаты"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Долгота
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {location.longitude.toFixed(6)}°
                </span>
              </div>

              {location.altitude !== null && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Высота над уровнем моря
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {location.altitude} м
                  </span>
                </div>
              )}

              {location.name && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Местоположение
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {location.name}
                  </span>
                </div>
              )}

              {location.city && location.country && (
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Населенный пункт
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {location.city}, {location.country}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                icon={<ExternalLink className="w-4 h-4" />}
                onClick={openInYandexMaps}
              >
                Открыть в Яндекс.Картах
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                fullWidth
                icon={<ExternalLink className="w-4 h-4" />}
                onClick={openInGoogleMaps}
              >
                Открыть в Google Maps
              </Button>
            </div>
          </div>

          {/* Карта */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
                <Navigation className="w-4 h-4 mr-2 text-purple-500" />
                Предпросмотр карты
              </h3>
              <button
                onClick={() => setMapVisible(!mapVisible)}
                className="p-1 rounded-full text-gray-500 hover:text-purple-500 dark:text-gray-400 dark:hover:text-purple-400 transition-colors"
              >
                {mapVisible ? (
                  <EyeOff className="w-4 h-4" title="Скрыть карту" />
                ) : (
                  <Eye className="w-4 h-4" title="Показать карту" />
                )}
              </button>
            </div>

            {mapVisible ? (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 aspect-[4/3]">
                <img 
                  src={staticMapUrl} 
                  alt="Карта места съемки" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/650x300?text=Карта+недоступна";
                  }}
                />
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 aspect-[4/3] flex items-center justify-center">
                <div className="text-gray-400 dark:text-gray-500 flex flex-col items-center">
                  <EyeOff className="w-8 h-8 mb-2" />
                  <span>Карта скрыта</span>
                  <span className="text-xs mt-1">Нажмите на иконку глаза, чтобы показать</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Информация о правовых аспектах в России */}
      <div className="p-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          Информация о конфиденциальности в России
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          В соответствии с Федеральным законом "О персональных данных" (№152-ФЗ), геолокационные данные в определенных контекстах 
          могут рассматриваться как персональные данные. Публикация фотографий с геометками, особенно сделанных рядом с вашим 
          домом или местом работы, может потенциально нарушить вашу приватность.
        </p>
      </div>
    </div>
  );
};

export default LocationViewer;